import { directive, Directive } from 'lit/directive.js';
import { LitElement, css, nothing, html } from 'lit';
import { state, query, property, customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const ARIA_IDREF_ATTRIBUTES = new Map([
    ['aria-activedescendant', 'ariaActivedescendantElement'],
    ['aria-controls', 'ariaControlsElements'],
    ['aria-describedby', 'ariaDescribedByElements'],
    ['aria-details', 'ariaDetailsElements'],
    ['aria-errormessage', 'ariaErrormessageElements'],
    ['aria-flowto', 'ariaFlowtoElements'],
    ['aria-labelledby', 'ariaLabelledByElements'],
    ['aria-owns', 'ariaOwnsElements'],
]);
function resolveAriaIdRefs(element, value) {
    const ids = value
        .trim()
        .split(/\s+/)
        .filter((id) => id.length > 0);
    if (ids.length === 0)
        return null;
    const root = element.getRootNode();
    let resolvedElements = [];
    if (root instanceof ShadowRoot) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else if (root instanceof Document) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    if (resolvedElements.filter((el) => el !== null).length === 0) {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    const validElements = resolvedElements.filter((el) => el !== null);
    return validElements.length === ids.length ? validElements : null;
}
function setInternalsProperty(el, name, resolvedElements) {
    if (name === 'aria-activedescendant') {
        const [firstElement] = resolvedElements;
        // eslint-disable-next-line no-param-reassign
        el[name] = firstElement;
    }
    else {
        // eslint-disable-next-line no-param-reassign
        el[name] = resolvedElements;
    }
}
class SetAttributesDirective extends Directive {
    update(part, [attributes]) {
        const el = part.element;
        const internals = el.internals ?? null;
        for (const [name, value] of Object.entries(attributes)) {
            if (value == null) {
                el.removeAttribute(name);
            }
            else {
                const internalsPropertyName = ARIA_IDREF_ATTRIBUTES.get(name);
                if (internalsPropertyName) {
                    const resolvedElements = resolveAriaIdRefs(el, String(value));
                    if (resolvedElements) {
                        if (internals) {
                            // Set on ElementInternals
                            setInternalsProperty(internals, internalsPropertyName, resolvedElements);
                        }
                        else {
                            // Set internals property on shadowdom item
                            el.setAttribute(name, String(value));
                            setInternalsProperty(el, internalsPropertyName, resolvedElements);
                        }
                    }
                }
                else {
                    // Non-ARIA IDREF attributes: set normally
                    el.setAttribute(name, String(value));
                }
            }
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_attributes) {
        return null;
    }
}
/* How to use:

<textarea ${setAttributes(this._ariaAttributes)} ... />
*/
const setAttributes = directive(SetAttributesDirective);

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};

/* eslint-disable no-console */
class Logger {
    constructor(tag) {
        this.tag = tag;
    }
    format(level, msg) {
        return [`[${this.tag}] ${msg}`];
    }
    warn(msg) {
        console.warn(...this.format('warn', msg));
    }
    error(msg) {
        console.error(...this.format('error', msg));
    }
    info(msg) {
        console.info(...this.format('info', msg));
    }
}

/**
 * Factory function per creare una base class estendibile
 * con stili personalizzati.
 */
class BaseComponent extends LitElement {
    constructor() {
        super();
        this.composeClass = clsx;
        this.logger = new Logger(this.tagName.toLowerCase());
    }
    get _ariaAttributes() {
        const attributes = {};
        for (const attr of this.getAttributeNames()) {
            if (attr === 'it-role') {
                attributes.role = this.getAttribute(attr);
            }
            else if (attr.startsWith('it-aria-')) {
                attributes[attr.replace(/^it-/, '')] = this.getAttribute(attr);
            }
        }
        return attributes;
    }
    // eslint-disable-next-line class-methods-use-this
    generateId(prefix) {
        return `${prefix}-${Math.random().toString(36).slice(2)}`;
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    addFocus(element) {
        // new TrackFocus(element); // per il momento è stato disattivato perchè ci sono le pseudo classi ::focus-visible per fare quello che fa TrackFocus. Si possono aggiungere regole css in bsi-italia 3 dato che stiamo facendo una breaking release di bsi.
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    get focusElement() {
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    connectedCallback() {
        super.connectedCallback();
        // generate internal _id
        const prefix = this.id?.length > 0 ? this.id : this.tagName.toLowerCase();
        this._id = this.generateId(prefix);
        this.ariaAttributesObserver = new MutationObserver((mutations) => {
            const shouldUpdate = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return mutation.attributeName === 'it-role' || mutation.attributeName.startsWith('it-aria-');
            });
            if (shouldUpdate) {
                this.requestUpdate();
            }
        });
        this.ariaAttributesObserver.observe(this, {
            attributes: true,
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        this.ariaAttributesObserver?.disconnect();
        this.ariaAttributesObserver = undefined;
        super.disconnectedCallback();
    }
}
const BaseLocalizedComponent = LocalizeMixin(BaseComponent);

//
// We store a WeakMap of forms + controls so we can keep references to all FormControl within a given form. As
// elements connect and disconnect to/from the DOM, their containing form is used as the key and the form control is
// added and removed from the form's set, respectively.
//
const formCollections = new WeakMap();
//
// We store a WeakMap of reportValidity() overloads so we can override it when form controls connect to the DOM and
// restore the original behavior when they disconnect.
//
const reportValidityOverloads = new WeakMap();
const checkValidityOverloads = new WeakMap();
//
// We store a Set of controls that users have interacted with. This allows us to determine the interaction state
// without littering the DOM with additional data attributes.
//
const userInteractedControls = new WeakSet();
//
// We store a WeakMap of interactions for each form control so we can track when all conditions are met for validation.
//
const interactions = new WeakMap();
/** A reactive controller to allow form controls to participate in form submission, validation, etc. */
class FormControlController {
    constructor(host, options) {
        this.submittedOnce = false;
        this.handleFormData = (event) => {
            // console.log('handleFormData');
            const disabled = this.options.disabled(this.host);
            const name = this.options.name(this.host);
            const value = this.options.value(this.host);
            const tagName = this.host.tagName.toLowerCase();
            // For buttons, we only submit the value if they were the submitter. This is currently done in doAction() by
            // injecting the name/value on a temporary button, so we can just skip them here.
            const isButton = tagName === 'it-button';
            if (this.host.isConnected &&
                !disabled &&
                !isButton &&
                typeof name === 'string' &&
                name.length > 0 &&
                typeof value !== 'undefined') {
                switch (tagName) {
                    case 'it-radio':
                        if (this.host.checked) {
                            event.formData.append(name, value);
                        }
                        break;
                    case 'it-checkbox':
                    case 'it-toggle':
                        if (this.host.checked) {
                            if (event.formData.getAll(name).indexOf(value) < 0) {
                                // handle group checkbox
                                event.formData.append(name, value);
                            }
                        }
                        break;
                    case 'it-checkbox-group':
                    case 'it-toggle-group':
                        // non settare valori in formData, perchè ogni singola checkbox setta il suo valore
                        break;
                    case 'it-upload':
                        // value is File[] — append each File object directly (not as string)
                        if (Array.isArray(value)) {
                            value.forEach((file) => {
                                event.formData.append(name, file);
                            });
                        }
                        break;
                    default:
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                event.formData.append(name, val.toString());
                            });
                        }
                        else {
                            event.formData.append(name, value.toString());
                        }
                }
            }
        };
        this.handleFormSubmit = (event) => {
            const disabled = this.options.disabled(this.host);
            const reportValidity = this.options.reportValidity;
            // Update the interacted state for all controls when the form is submitted
            if (this.form && !this.form.noValidate) {
                formCollections.get(this.form)?.forEach((control) => {
                    this.setUserInteracted(control, true);
                });
            }
            const resReportValidity = reportValidity(this.host);
            if (this.form && !this.form.noValidate && !disabled && !resReportValidity) {
                event.preventDefault();
                // event.stopImmediatePropagation(); // se lo attiviamo, valida un campo alla volta
                // Scroll al primo controllo non valido
                const formControls = formCollections.get(this.form);
                if (formControls) {
                    for (const control of formControls) {
                        if (!control.validity?.valid) {
                            // Scroll smooth verso il controllo non valido
                            control.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            break;
                        }
                    }
                }
            }
            this.submittedOnce = true;
        };
        this.handleFormReset = () => {
            this.options.setValue(this.host, '');
            this.submittedOnce = false;
            this.setUserInteracted(this.host, false);
            interactions.set(this.host, []);
        };
        this.handleInteraction = (event) => {
            const emittedEvents = interactions.get(this.host);
            if (!emittedEvents.includes(event.type)) {
                emittedEvents.push(event.type);
            }
            // Mark it as user-interacted as soon as all associated events have been emitted
            if (emittedEvents.length === this.options.assumeInteractionOn.length) {
                this.setUserInteracted(this.host, true);
            }
        };
        this.checkFormValidity = () => {
            // console.log('checkFormValidity');
            //
            // This is very similar to the `reportFormValidity` function, but it does not trigger native constraint validation
            // Allow the user to simply check if the form is valid and handling validity in their own way.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.checkValidity === 'function') {
                        if (!element.checkValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        this.reportFormValidity = () => {
            // console.log('reportFormValidity');
            //
            // FormControl work hard to act like regular form controls. They support the Constraint Validation API
            // and its associated methods such as setCustomValidity() and reportValidity(). However, the HTMLFormElement also
            // has a reportValidity() method that will trigger validation on all child controls. Since we're not yet using
            // ElementInternals, we need to overload this method so it looks for any element with the reportValidity() method.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.reportValidity === 'function') {
                        if (!element.reportValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        (this.host = host).addController(this);
        this.options = {
            form: (input) => {
                // If there's a form attribute, use it to find the target form by id
                // Controls may not always reflect the 'form' property. For example, `<it-button>` doesn't reflect.
                const formId = input.form;
                if (formId) {
                    const root = input.getRootNode();
                    const form = root.querySelector(`#${formId}`);
                    if (form) {
                        return form;
                    }
                }
                return input.closest('form');
            },
            name: (input) => input.name,
            value: (input) => input.value,
            disabled: (input) => input.disabled ?? false,
            reportValidity: (input) => typeof input.reportValidity === 'function' ? input.reportValidity() : true,
            checkValidity: (input) => (typeof input.checkValidity === 'function' ? input.checkValidity() : true),
            setValue: (input, value) => {
                // eslint-disable-next-line no-param-reassign
                input.value = value;
            },
            assumeInteractionOn: ['it-input'],
            ...options,
        };
    }
    hostConnected() {
        const form = this.options.form(this.host);
        if (form) {
            this.attachForm(form);
        }
        // Listen for interactions
        interactions.set(this.host, []);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.addEventListener(event, this.handleInteraction);
        });
    }
    hostDisconnected() {
        this.detachForm();
        // Clean up interactions
        interactions.delete(this.host);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.removeEventListener(event, this.handleInteraction);
        });
    }
    hostUpdated() {
        const form = this.options.form(this.host);
        // Detach if the form no longer exists
        if (!form) {
            this.detachForm();
        }
        // If the form has changed, reattach it
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.hasUpdated) {
            this.setValidity(this.host.validity.valid);
        }
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // Add this element to the form's collection
            if (formCollections.has(this.form)) {
                formCollections.get(this.form).add(this.host);
            }
            else {
                formCollections.set(this.form, new Set([this.host]));
            }
            this.form.addEventListener('formdata', this.handleFormData);
            this.form.addEventListener('submit', this.handleFormSubmit);
            this.form.addEventListener('reset', this.handleFormReset);
            // Overload the form's reportValidity() method so it looks at FormControl
            if (!reportValidityOverloads.has(this.form)) {
                reportValidityOverloads.set(this.form, this.form.reportValidity);
                this.form.reportValidity = () => this.reportFormValidity();
            }
            // Overload the form's checkValidity() method so it looks at FormControl
            if (!checkValidityOverloads.has(this.form)) {
                checkValidityOverloads.set(this.form, this.form.checkValidity);
                this.form.checkValidity = () => this.checkFormValidity();
            }
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (!this.form)
            return;
        const formCollection = formCollections.get(this.form);
        if (!formCollection) {
            return;
        }
        this.submittedOnce = false;
        // Remove this host from the form's collection
        formCollection.delete(this.host);
        // Check to make sure there's no other form controls in the collection. If we do this
        // without checking if any other controls are still in the collection, then we will wipe out the
        // validity checks for all other elements.
        // see: https://github.com/shoelace-style/shoelace/issues/1703
        if (formCollection.size <= 0) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
            this.form.removeEventListener('reset', this.handleFormReset);
            // Remove the overload and restore the original method
            if (reportValidityOverloads.has(this.form)) {
                this.form.reportValidity = reportValidityOverloads.get(this.form);
                reportValidityOverloads.delete(this.form);
            }
            if (checkValidityOverloads.has(this.form)) {
                this.form.checkValidity = checkValidityOverloads.get(this.form);
                checkValidityOverloads.delete(this.form);
            }
            // So it looks weird here to not always set the form to undefined. But I _think_ if we unattach this.form here,
            // we end up in this fun spot where future validity checks don't have a reference to the form validity handler.
            // First form element in sets the validity handler. So we can't clean up `this.form` until there are no other form elements in the form.
            this.form = undefined;
        }
    }
    // eslint-disable-next-line class-methods-use-this
    setUserInteracted(el, hasInteracted) {
        if (hasInteracted) {
            userInteractedControls.add(el);
        }
        else {
            userInteractedControls.delete(el);
        }
        el.requestUpdate();
    }
    doAction(type, submitter) {
        // console.log('doaction', type);
        if (this.form) {
            const button = document.createElement('button');
            button.type = type;
            button.style.position = 'absolute';
            button.style.width = '0';
            button.style.height = '0';
            button.style.clipPath = 'inset(50%)';
            button.style.overflow = 'hidden';
            button.style.whiteSpace = 'nowrap';
            // Pass name, value, and form attributes through to the temporary button
            if (submitter) {
                button.name = submitter.name;
                button.value = submitter.value;
                ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'].forEach((attr) => {
                    if (submitter.hasAttribute(attr)) {
                        button.setAttribute(attr, submitter.getAttribute(attr));
                    }
                });
            }
            this.form.append(button);
            button.click();
            button.remove();
        }
    }
    /** Returns the associated `<form>` element, if one exists. */
    getForm() {
        return this.form ?? null;
    }
    /** Resets the form, restoring all the control to their default value */
    reset(submitter) {
        this.doAction('reset', submitter);
    }
    /** Submits the form, triggering validation and form data injection. */
    submit(submitter) {
        // Calling form.submit() bypasses the submit event and constraint validation. To prevent this, we can inject a
        // native submit button into the form, "click" it, then remove it to simulate a standard form submission.
        this.doAction('submit', submitter);
    }
    /**
     * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
     * the host element immediately, i.e. before Lit updates the component in the next update.
     */
    setValidity(isValid) {
        const host = this.host;
        const hasInteracted = Boolean(userInteractedControls.has(host));
        const required = Boolean(host.required);
        //
        // We're mapping the following "states" to data attributes. In the future, we can use ElementInternals.states to
        // create a similar mapping, but instead of [data-invalid] it will look like :--invalid.
        //
        // See this RFC for more details: https://github.com/shoelace-style/shoelace/issues/1011
        //
        host.toggleAttribute('data-required', required);
        host.toggleAttribute('data-optional', !required);
        host.toggleAttribute('data-invalid', !isValid);
        host.toggleAttribute('data-valid', isValid);
        host.toggleAttribute('data-user-invalid', !isValid && hasInteracted);
        host.toggleAttribute('data-user-valid', isValid && hasInteracted);
    }
    userInteracted() {
        const hasInteracted = Boolean(userInteractedControls.has(this.host));
        return hasInteracted;
    }
    /**
     * Updates the form control's validity based on the current value of `host.validity.valid`. Call this when anything
     * that affects constraint validation changes so the component receives the correct validity states.
     */
    updateValidity() {
        const host = this.host;
        this.setValidity(host.validity.valid);
    }
    /**
     * Dispatches a non-bubbling, cancelable custom event of type `it-invalid`.
     * If the `it-invalid` event will be cancelled then the original `invalid`
     * event (which may have been passed as argument) will also be cancelled.
     * If no original `invalid` event has been passed then the `it-invalid`
     * event will be cancelled before being dispatched.
     */
    emitInvalidEvent(originalInvalidEvent) {
        const itInvalidEvent = new CustomEvent('it-invalid', {
            bubbles: false,
            composed: false,
            cancelable: true,
            detail: {},
        });
        if (!originalInvalidEvent) {
            itInvalidEvent.preventDefault();
        }
        if (!this.host.dispatchEvent(itInvalidEvent)) {
            originalInvalidEvent?.preventDefault();
        }
    }
}

const translation$2 = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    validityRequired: 'Questo campo è obbligatorio.',
    validityGroupRequired: "Scegli almeno un'opzione",
    validityPattern: 'Il valore non corrisponde al formato richiesto.',
    validityMinlength: 'Il valore deve essere lungo almeno {minlength} caratteri.',
    validityMaxlength: 'Il valore deve essere lungo al massimo {maxlength} caratteri.',
};

registerTranslation(translation$2);
class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);

if (typeof window !== 'undefined') {
    window._itAnalytics = window._itAnalytics || {};
    window._itAnalytics = {
        ...window._itAnalytics,
        version: '1.0.0-beta.3',
    };
}

var styles = css`.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.avatar {
  --bsi-avatar-background: var(--bsi-color-background-secondary-lighter);
  --bsi-avatar-dot-border-color: var(--bsi-color-border-inverse);
  --bsi-avatar-dot-border-size: 2px;
  --bsi-avatar-dot-offset-bottom: 0;
  --bsi-avatar-dot-offset-right: 0;
  --bsi-avatar-dot-offset-top: 4px;
  --bsi-avatar-dot-size: 12px;
  --bsi-avatar-font-size: var(--bsi-font-size-3);
  --bsi-avatar-font-weight: var(--bsi-font-weight-solid);
  --bsi-avatar-icon-size: var(--bsi-icon-size-s);
  --bsi-avatar-overlay-background: var(--bsi-color-background-emphasis);
  --bsi-avatar-radius: var(--bsi-radius-circle);
  --bsi-avatar-size: 2.5rem;
  --bsi-avatar-text-color: var(--bsi-color-text-secondary);
  --bsi-avatar-dropdown-margin-left: -24px;
  --bsi-avatar-dropdown-margin-top: 24px;
}

/* stylelint-disable-next-line no-duplicate-selectors */
.avatar {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--bsi-avatar-size);
  height: var(--bsi-avatar-size);
  border-radius: var(--bsi-avatar-radius);
  background: var(--bsi-avatar-background);
  color: var(--bsi-avatar-text-color);
  transition: background-color var(--bsi-transition-instant);
}
.avatar.avatar-primary {
  --bsi-avatar-background: var(--bsi-theme-primary);
  --bsi-avatar-text-color: var(--bsi-color-text-inverse);
}
.avatar.avatar-secondary {
  --bsi-avatar-background: var(--bsi-color-background-secondary);
  --bsi-avatar-text-color: var(--bsi-color-text-inverse);
}
.avatar.size-xs {
  --bsi-avatar-dot-border-size: 1px;
  --bsi-avatar-font-size: 0.625rem;
  --bsi-avatar-size: 1rem;
  --bsi-avatar-icon-size: 0.625rem;
}
.avatar.size-sm {
  --bsi-avatar-dot-border-size: 1px;
  --bsi-avatar-font-size: var(--bsi-font-size-1);
  --bsi-avatar-size: 1.5rem;
  --bsi-avatar-icon-size: var(--bsi-icon-size-xs);
}
.avatar.size-md {
  --bsi-avatar-font-size: var(--bsi-font-size-4);
  --bsi-avatar-size: 2.5rem;
  --bsi-avatar-icon-size: var(--bsi-icon-size-s);
  --bsi-avatar-dot-offset-right: -2px;
}
.avatar.size-lg {
  --bsi-avatar-font-size: var(--bsi-font-size-6);
  --bsi-avatar-size: 3.5rem;
  --bsi-avatar-icon-size: var(--bsi-icon-size-m);
  --bsi-avatar-dot-offset-right: -2px;
  --bsi-avatar-dot-size: 14px;
}
.avatar.size-xl {
  --bsi-avatar-font-size: var(--bsi-font-size-9);
  --bsi-avatar-size: 5rem;
  --bsi-avatar-icon-size: var(--bsi-icon-size-l);
  --bsi-avatar-dot-size: 16px;
}
.avatar.size-xxl {
  --bsi-avatar-font-size: var(--bsi-font-size-11);
  --bsi-avatar-size: 7rem;
  --bsi-avatar-icon-size: var(--bsi-icon-size-xl);
  --bsi-avatar-dot-size: 20px;
  --bsi-avatar-dot-offset-right: 4px;
}
.avatar-wrapper {
  position: relative;
  z-index: 1;
}
.avatar-presence, .avatar-status {
  position: absolute;
  right: var(--bsi-avatar-dot-offset-right);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--bsi-avatar-dot-size);
  height: var(--bsi-avatar-dot-size);
  border: var(--bsi-avatar-dot-border-size) solid var(--bsi-avatar-dot-border-color);
  border-radius: var(--bsi-avatar-radius);
  background: hsl(210, 3%, 85%);
  color: var(--bsi-color-text-inverse);
}
.avatar-presence .icon, .avatar-status .icon {
  stroke-width: 2px;
  stroke: var(--bsi-icon-inverse);
}
.avatar-presence.active, .avatar-status.approved {
  background: var(--bsi-color-background-success);
}
.avatar-presence.busy, .avatar-status.declined {
  background: hsl(350, 60%, 50%);
}
.avatar-presence {
  bottom: 8px;
}
.avatar-presence.hidden:after {
  position: absolute;
  top: 50%;
  left: 50%;
  content: "";
  width: calc(100% - 4px);
  height: calc(100% - 4px);
  border-radius: 50%;
  background: var(--bsi-color-background-inverse);
  transform: translateX(-50%) translateY(-50%);
}
.avatar-status {
  top: var(--bsi-avatar-dot-offset-top);
}
.avatar-status.notify {
  background: var(--bsi-theme-primary);
}
.avatar img {
  width: 100%;
  height: 100%;
  border-radius: var(--bsi-avatar-radius);
  object-fit: cover;
  object-position: center;
}
.avatar p {
  margin: 0;
  color: var(--bsi-avatar-text-color);
  font-size: var(--bsi-avatar-font-size);
  font-weight: var(--bsi-avatar-font-weight);
  line-height: 1;
}
.avatar .icon {
  width: var(--bsi-avatar-icon-size);
  height: var(--bsi-avatar-icon-size);
}
.avatar ~ span {
  margin-left: var(--bsi-spacing-xxs);
}

a.avatar::after,
a .avatar::after {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  content: "";
  display: block;
  width: 100%;
  height: 100%;
  border-radius: var(--bsi-avatar-radius);
  opacity: 0;
  background: var(--bsi-color-background-emphasis);
  transition: opacity 0.2s;
}
a.avatar:hover::after,
a .avatar:hover::after {
  opacity: 0.25;
}

a.avatar,
a.avatar:hover,
a.avatar:hover p,
a .avatar:hover,
a .avatar:hover p {
  text-decoration: none;
}

.avatar-dropdown {
  position: relative;
  overflow: visible;
}
.avatar-dropdown .dropdown {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 0;
}
.avatar-dropdown .dropdown-menu {
  margin-left: var(--bsi-avatar-dropdown-margin-left) !important;
  margin-top: var(--bsi-avatar-dropdown-margin-top) !important;
}
.avatar-dropdown .btn-dropdown {
  padding: 0;
  color: inherit;
  font-size: var(--bsi-avatar-font-size);
  line-height: 0;
}
.avatar-dropdown .btn-dropdown:focus {
  box-shadow: none;
}
.avatar-dropdown .link-list {
  white-space: nowrap;
}

.avatar-extra-text {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 16px;
}
.avatar-extra-text .avatar {
  flex-shrink: 0;
}
.avatar-extra-text .extra-text, .avatar-extra-text slot[name=extra-text]::slotted(*) {
  margin-left: var(--bsi-spacing-s);
}
.avatar-extra-text .extra-text h3, .avatar-extra-text slot[name=extra-text]::slotted(*) h3,
.avatar-extra-text .extra-text h4,
.avatar-extra-text slot[name=extra-text]::slotted(*) h4 {
  margin: 0;
  font-size: var(--bsi-heading-6-font-size);
}
.avatar-extra-text .extra-text h3 a, .avatar-extra-text slot[name=extra-text]::slotted(*) h3 a,
.avatar-extra-text .extra-text h4 a,
.avatar-extra-text slot[name=extra-text]::slotted(*) h4 a {
  display: inline-block;
}
.avatar-extra-text .extra-text p, .avatar-extra-text slot[name=extra-text]::slotted(*) p,
.avatar-extra-text .extra-text time,
.avatar-extra-text slot[name=extra-text]::slotted(*) time {
  margin: 0;
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-caption-font-size);
  text-transform: uppercase;
}

.avatar-group > li {
  margin-bottom: var(--bsi-spacing-s);
  display: flex;
}
.avatar-group > li:last-child {
  margin-bottom: 0;
}
.avatar-group > li .list-item {
  padding: 0;
}

.avatar-group-stacked {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  margin: 0;
  padding: 0;
}
.avatar-group-stacked li {
  list-style-type: none;
  line-height: 0;
}
.avatar-group-stacked li > .avatar {
  margin-left: -6px;
  border: 2px solid var(--bsi-color-border-inverse);
}

:host {
  display: inline-flex;
  --bsi-avatar-background: var(--bsi-color-background-secondary-lighter);
  --bsi-avatar-text-color: var(--bsi-color-text-secondary);
  --bsi-avatar-font-size: var(--bsi-font-size-3);
  --bsi-avatar-font-weight: var(--bsi-font-weight-solid);
  --bsi-avatar-icon-size: var(--bsi-icon-size-s);
  --bsi-avatar-radius: var(--bsi-radius-circle);
  --bsi-avatar-size: 2.5rem;
  --bsi-avatar-dot-size: 12px;
  --bsi-avatar-dot-border-size: 2px;
  --bsi-avatar-dot-border-color: var(--bsi-color-border-inverse);
  --bsi-avatar-dot-offset-right: 0;
  --bsi-avatar-dot-offset-top: 4px;
  --bsi-avatar-dot-offset-bottom: 8px;
}
:host a.avatar,
:host a .avatar {
  text-decoration: none;
}
:host a.avatar:hover,
:host a .avatar:hover {
  text-decoration: none;
}
:host .avatar-presence {
  bottom: var(--bsi-avatar-dot-offset-bottom);
}
:host .avatar-presence,
:host .avatar-status {
  box-sizing: border-box;
}

:host([size=xs]),
:host([size=sm]) {
  --bsi-avatar-dot-border-size: 1px;
}

:host([size=md]) {
  --bsi-avatar-dot-offset-right: -4px;
}

:host([size=lg]) {
  --bsi-avatar-dot-size: 14px;
  --bsi-avatar-dot-offset-right: -4px;
}

:host([size=xl]) {
  --bsi-avatar-dot-size: 16px;
}

:host([size=xxl]) {
  --bsi-avatar-dot-size: 20px;
  --bsi-avatar-dot-offset-right: 4px;
}

:host([type=text]:not([href])),
:host([type=text][href=""]) {
  cursor: default;
  pointer-events: none;
}

it-icon::part(icon) {
  width: var(--bsi-avatar-icon-size);
  height: var(--bsi-avatar-icon-size);
}

it-icon {
  display: inline-flex;
}

it-icon.presence-icon::part(icon),
it-icon.status-icon::part(icon) {
  width: 12px;
  height: 12px;
  stroke: var(--bsi-icon-inverse);
  stroke-width: 2px;
}

.extra-text, slot[name=extra-text]::slotted(*) {
  margin-left: var(--bsi-spacing-s);
}
.extra-text h3, slot[name=extra-text]::slotted(*) h3,
.extra-text h4,
slot[name=extra-text]::slotted(*) h4 {
  margin: 0;
  font-size: var(--bsi-heading-6-font-size) !important;
}
.extra-text h3 a, slot[name=extra-text]::slotted(*) h3 a,
.extra-text h4 a,
slot[name=extra-text]::slotted(*) h4 a {
  display: inline-block;
}
.extra-text p, slot[name=extra-text]::slotted(*) p,
.extra-text time,
slot[name=extra-text]::slotted(*) time {
  margin: 0;
  color: var(--bsi-color-text-secondary);
  font-size: var(--bsi-caption-font-size);
  text-transform: uppercase;
}

.avatar-wrapper {
  display: inline-flex;
}`;

const translation$1 = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    // Presence translations
    avatar_presence_active: 'attivo',
    avatar_presence_busy: 'non disponibile',
    avatar_presence_hidden: 'invisibile',
    avatar_presence_label: 'Presenza',
    // Status translations
    avatar_status_approved: 'approvato',
    avatar_status_declined: 'respinto',
    avatar_status_notify: 'notifica',
    avatar_status_label: 'Stato',
};

const translation = {
    $code: 'en',
    $name: 'English',
    $dir: 'ltr',
    // Presence translations
    avatar_presence_active: 'active',
    avatar_presence_busy: 'busy',
    avatar_presence_hidden: 'hidden',
    avatar_presence_label: 'Presence',
    // Status translations
    avatar_status_approved: 'approved',
    avatar_status_declined: 'declined',
    avatar_status_notify: 'notify',
    avatar_status_label: 'Status',
};

registerTranslation(translation$1);
registerTranslation(translation);
// Avatar dropdowns (e.g. the "+N" group balloon) must not show the expand
// caret — it pushes the label off-centre. Mirrors Bootstrap Italia, where the
// avatar dropdown is a bare btn-dropdown with no caret. The "+N" trigger is a
// tiny circle, so the popover arrow is centred on it rather than using the
// fixed notch position standard dropdowns rely on. See issue #431.
const onDropdownSlotChange = (event) => {
    const slot = event.target;
    slot.assignedElements().forEach((el) => {
        if (el.tagName.toLowerCase() === 'it-dropdown') {
            el.setAttribute('hide-expand-icon', '');
            el.setAttribute('center-arrow', '');
        }
    });
};
let ItAvatar = class ItAvatar extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.size = 'md';
        this.variant = '';
        this.presence = '';
        this.status = '';
        this.type = '';
        this.src = '';
        this.alt = '';
        this.text = '';
        this.icon = '';
        this._imageLoadError = false;
        this.href = '';
        this.avatarTitle = '';
        this._onImageError = () => {
            this._imageLoadError = true;
            this.requestUpdate();
        };
        this.onExtraTextSlotChange = () => {
            const extraTextSlot = this.shadowRoot?.querySelector('slot[name="extra-text"]');
            if (extraTextSlot?.assignedElements().length > 0) {
                extraTextSlot?.assignedElements().forEach((el) => {
                    el.classList.add('extra-text');
                });
            }
        };
    }
    get __hasExplicitSize() {
        return this.hasAttribute('size');
    }
    get focusElement() {
        // Se c'è un link, usa quello
        const link = this.shadowRoot?.querySelector('a.avatar');
        if (link)
            return link;
        return null;
    }
    getAutoType() {
        // Se type è specificato esplicitamente, usalo
        if (this.type !== '') {
            return this.type;
        }
        // Determinazione automatica:
        // 1. Se c'è src → image
        // 2. Se c'è icon → icon
        // 3. Altrimenti → text
        if (this.src) {
            return 'image';
        }
        if (this.icon) {
            return 'icon';
        }
        return 'text';
    }
    getInitials() {
        if (this.text) {
            const words = this.text.trim().split(/\s+/);
            if (this.size === 'xs' || this.size === 'sm') {
                return words[0]?.charAt(0)?.toUpperCase() || '';
            }
            return (words
                .slice(0, 2)
                .map((word) => word.charAt(0)?.toUpperCase())
                .join('') || '');
        }
        return '';
    }
    getInitialsFromName(name) {
        const words = name.trim().split(/\s+/);
        if (this.size === 'xs' || this.size === 'sm') {
            return words[0]?.charAt(0)?.toUpperCase() || '';
        }
        return (words
            .slice(0, 2)
            .map((word) => word.charAt(0)?.toUpperCase())
            .join('') || '');
    }
    getAvatarWrapperClasses() {
        return classMap({
            avatar: true,
            [`size-${this.size}`]: !!this.size,
            [`avatar-${this.variant}`]: !!this.variant,
            'avatar-extra-text': this.hasExtraTextContent,
            'avatar-dropdown': this.type === 'dropdown',
        });
    }
    getAvatarClasses() {
        return classMap({
            avatar: true,
            [`size-${this.size}`]: !!this.size,
            [`avatar-${this.variant}`]: !!this.variant,
            'avatar-dropdown': this.type === 'dropdown',
        });
    }
    getPresenceClasses() {
        return classMap({
            'avatar-presence': true,
            [this.presence]: !!this.presence,
        });
    }
    getStatusClasses() {
        return classMap({
            'avatar-status': true,
            [this.status]: !!this.status,
        });
    }
    renderPresence() {
        if (!this.presence)
            return nothing;
        const presenceText = this.$t(`avatar_presence_${this.presence}`);
        const presenceLabel = this.$t('avatar_presence_label');
        // Icone di default per i diversi stati di presenza
        let presenceIcon = '';
        switch (this.presence) {
            case 'busy':
                presenceIcon = 'it-minus';
                break;
            case 'active':
            case 'hidden':
                presenceIcon = '';
                break;
            default:
                presenceIcon = '';
        }
        return html `
      <div class="${this.getPresenceClasses()}">
        ${presenceIcon
            ? html `<it-icon name="${presenceIcon}" size="xs" variant="white" class="presence-icon"></it-icon>`
            : nothing}
        <span class="visually-hidden">${presenceLabel}: ${presenceText}</span>
      </div>
    `;
    }
    renderStatus() {
        if (!this.status)
            return nothing;
        const statusText = this.$t(`avatar_status_${this.status}`);
        const statusLabel = this.$t('avatar_status_label');
        // Icone di default per i diversi stati dello status
        let statusIcon = '';
        switch (this.status) {
            case 'approved':
                statusIcon = 'it-check';
                break;
            case 'declined':
                statusIcon = 'it-close';
                break;
            case 'notify':
                statusIcon = '';
                break;
            default:
                statusIcon = '';
        }
        return html `
      <div class="${this.getStatusClasses()}">
        ${statusIcon
            ? html `<it-icon name="${statusIcon}" size="xs" variant="white" class="status-icon"></it-icon>`
            : nothing}
        <span class="visually-hidden">${statusLabel}: ${statusText}</span>
      </div>
    `;
    }
    renderImage() {
        if (!this.src || this._imageLoadError) {
            // Fallback su iniziali quando immagine non disponibile
            const fallbackText = this.alt || this.text || '';
            if (fallbackText) {
                const initials = this.getInitialsFromName(fallbackText);
                return html `
          <p aria-hidden="true">${initials}</p>
          <span class="visually-hidden">${fallbackText}</span>
        `;
            }
            return nothing;
        }
        return html `
      <img
        src="${this.src}"
        alt="${this.alt || this.text || ''}"
        @error="${this._onImageError}"
        ${this.text ? 'aria-hidden="true"' : ''}
      />
      ${this.text ? html `<span class="visually-hidden">${this.text}</span>` : nothing}
    `;
    }
    willUpdate(changedProperties) {
        super.willUpdate(changedProperties);
        // Reset image error when src changes
        if (changedProperties.has('src')) {
            this._imageLoadError = false;
        }
    }
    renderText() {
        const initials = this.getInitials();
        if (!initials)
            return nothing;
        return html `
      <p aria-hidden="true">${initials}</p>
      ${this.avatarTitle ? html `<span class="visually-hidden">${this.avatarTitle}</span>` : nothing}
    `;
    }
    renderIcon() {
        if (!this.icon)
            return nothing;
        return html `
      <it-icon name="${this.icon}"></it-icon>
      ${this.avatarTitle ? html `<span class="visually-hidden">${this.avatarTitle}</span>` : nothing}
    `;
    }
    renderAvatarContent() {
        const autoType = this.getAutoType();
        const content = html `
      ${autoType === 'image' ? this.renderImage() : nothing} ${autoType === 'text' ? this.renderText() : nothing}
      ${autoType === 'icon' ? this.renderIcon() : nothing}
      ${autoType === 'dropdown'
            ? html `<slot name="avatar-dropdown-content" @slotchange="${onDropdownSlotChange}"></slot>`
            : nothing}
    `;
        return content;
    }
    updated() {
        const autoType = this.getAutoType();
        // Warning accessibilità automatici
        if (autoType === 'image' && this.src && !this.alt && !this.text) {
            this.logger.warn('Avatar: immagine fornita senza testo alternativo (alt) o contenuto testuale (text). ' +
                'Questo impatta negativamente l\'accessibilità. Aggiungi alt="..." o text="..."');
        }
        if (autoType === 'icon' && this.icon && !this.avatarTitle && !this.text && !this.alt) {
            this.logger.warn('Avatar: icona fornita senza testo alternativo. ' +
                'Aggiungi avatar-title="..." o text="..." o alt="..." per l\'accessibilità.');
        }
        if (autoType === 'text' && !this.text) {
            this.logger.warn('Avatar: tipo text rilevato ma nessun contenuto testuale fornito. Aggiungi text="..."');
        }
        if (this.href && !this.avatarTitle && !this.text) {
            this.logger.warn('Avatar: link fornito senza titolo o contenuto testuale. ' +
                'Aggiungi avatar-title="..." o text="..." per l\'accessibilità.');
        }
        if (this.src && this.icon) {
            this.logger.warn('Avatar: sia src che icon sono specificati. Il tipo verrà determinato automaticamente come "image".');
        }
    }
    get hasExtraTextContent() {
        const extraTextSlot = this.shadowRoot?.querySelector('slot[name="extra-text"]');
        return extraTextSlot?.assignedElements().length > 0 || false;
    }
    getWrapperClasses() {
        return classMap({
            'avatar-wrapper': true,
            'avatar-extra-text': this.hasExtraTextContent,
        });
    }
    render() {
        const avatarContent = this.renderAvatarContent();
        const avatar = this.href && this.type !== 'dropdown'
            ? html `
            <a
              href="${this.href}"
              class="${this.getAvatarWrapperClasses()}"
              title="${ifDefined(this.avatarTitle || this.text || undefined)}"
              part="avatar focusable"
              ${setAttributes(this._ariaAttributes)}
            >
              ${avatarContent}
            </a>
          `
            : html `<div class="${this.getAvatarClasses()}" part="avatar">${avatarContent}</div>`;
        return html `
      <div class="${this.getWrapperClasses()}">
        ${avatar}
        <slot name="extra-text" @slotchange="${this.onExtraTextSlotChange}" part="extra-text"></slot>
        <slot name="avatar-dropdown-content" part="avatar-dropdown-content"></slot>
        <slot name="presence" part="presence">${this.renderPresence()}</slot>
        <slot name="status" part="status">${this.renderStatus()}</slot>
      </div>
    `;
    }
};
ItAvatar.styles = styles;
ItAvatar.shadowRootOptions = {
    ...BaseLocalizedComponent.shadowRootOptions,
    delegatesFocus: true,
};
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItAvatar.prototype, "size", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItAvatar.prototype, "variant", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItAvatar.prototype, "presence", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItAvatar.prototype, "status", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], ItAvatar.prototype, "type", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItAvatar.prototype, "src", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItAvatar.prototype, "alt", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItAvatar.prototype, "text", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItAvatar.prototype, "icon", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItAvatar.prototype, "href", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'avatar-title' }),
    __metadata("design:type", Object)
], ItAvatar.prototype, "avatarTitle", void 0);
ItAvatar = __decorate([
    customElement('it-avatar')
], ItAvatar);

export { ItAvatar };
//# sourceMappingURL=it-avatar.js.map
